import { wrapIndex } from '../core/math';
import type { Corner } from './brakingzones';
import type { Track } from './generate';
import { PointGrid } from './pointgrid';
import { fnv1a, mulberry32 } from './rng';

/**
 * Trackside sponsorship — who advertises on a circuit and where their panels
 * sit. Placement only: nothing here touches the centerline, the physics or a
 * lap time, so a date still regenerates byte-identically (`CLAUDE.md`). The
 * two renderers both read these placements, so an ad is in the same world
 * position whichever one is drawing (`docs/3d-assets.md`, "Both renderers must
 * agree").
 *
 * Everything is derived from `track.key` through its own RNG stream rather
 * than from `track.seed`'s generator stream, so adding, removing or reordering
 * a sponsor can never perturb a layout.
 */

export interface Sponsor {
  id: string;
  /** The wordmark, exactly as it should read on a panel. */
  name: string;
  /** Panel background. */
  bg: string;
  /** Wordmark colour. */
  fg: string;
  /** Thin edge stripe, so a panel reads as a panel against grass or armco. */
  trim: string;
  /** CSS font family list. Size and weight are supplied per panel. */
  family: string;
  /** 400-900, the wordmark's weight. */
  weight: number;
  /** Wordmark is set in capitals. */
  upper?: boolean;
  /** Slanted wordmark. */
  italic?: boolean;
  /** Letter spacing, em. */
  tracking?: number;
  /**
   * Wordmark is a chrome gradient rather than the flat `fg` colour — bands of
   * near-white through mid-grey, the classic engraved-metal sponsor decal.
   * `fg` is still the colour used everywhere a single flat swatch is needed
   * (a vector mark's stroke, a future non-canvas consumer); the gradient is a
   * paint-time-only embellishment layered on top of it.
   */
  metallic?: boolean;
  /**
   * Sold to every circuit, placed first, and so first in line for the paint at
   * the corners and the barrier along the start/finish straight — the two
   * places a name is actually looked at. Keep this list short; the point of the
   * rotation is that circuits do not all read the same.
   */
  anchor?: boolean;
}

/**
 * The paddock. Colours are the brands' own as described, not sampled from
 * anyone's assets, and the wordmarks are set in stock system faces — these are
 * fictional-circuit signage, not reproductions of a real identity.
 */
export const SPONSORS: readonly Sponsor[] = [
  {
    id: 'aldan-tyres',
    name: 'Aldan Tyres',
    // Tyre-trade yellow and blue, in the heavy slanted grotesque the trade uses.
    bg: '#fce500',
    fg: '#27509b',
    trim: '#27509b',
    family: '"Avenir Next", Futura, "Trebuchet MS", "DejaVu Sans", sans-serif',
    weight: 900,
    italic: true,
    upper: true,
    tracking: 0.02,
    anchor: true,
  },
  {
    id: 'nurgun-botur',
    name: 'Nurgun Botur Racing',
    // A junior-formula team's silver-on-red, in the compressed poster face team
    // liveries reach for — the wordmark a chrome decal rather than flat paint.
    bg: '#c8102e',
    fg: '#c9ced3',
    trim: '#c9ced3',
    family: 'Impact, Haettenschweiler, "Arial Black", "DejaVu Sans", sans-serif',
    weight: 900,
    upper: true,
    tracking: 0.03,
    anchor: true,
    metallic: true,
  },
  {
    id: 'yakutia-motor',
    name: 'Yakutia Motor Corp',
    bg: '#0b0b0c',
    fg: '#ffd400',
    trim: '#ffd400',
    family: '"Arial Narrow", "Helvetica Neue Condensed", Impact, sans-serif',
    weight: 700,
    upper: true,
    tracking: 0.06,
  },
  {
    id: 'visit-yakutsk',
    name: '#VisitYakutsk',
    // The light blue of the Sakha Republic's flag.
    bg: '#1b9bd8',
    fg: '#ffffff',
    trim: '#ffffff',
    family: '"Trebuchet MS", Verdana, "DejaVu Sans", sans-serif',
    weight: 800,
    tracking: 0.01,
  },
  {
    id: 'ykt-motors',
    name: 'YKT Motors',
    // British racing green and cream, set in a serif — a heritage marque's
    // signage, and the only serif on the roster.
    bg: '#004225',
    fg: '#f2ead3',
    trim: '#f2ead3',
    family: '"Palatino Linotype", Palatino, Georgia, "DejaVu Serif", serif',
    weight: 700,
    upper: true,
    tracking: 0.1,
  },
  {
    id: 'baishev',
    name: 'Baishev Instruments',
    // A test-and-measurement house's red wordmark on white — the identity is
    // the type, not a coloured field, so the panel is inverted from the rest.
    bg: '#ffffff',
    fg: '#cc0000',
    trim: '#cc0000',
    family: '"Segoe UI", "Lucida Grande", Tahoma, "DejaVu Sans", sans-serif',
    weight: 600,
    tracking: 0,
  },
  {
    id: 'aisen',
    name: 'Aisen Insurance',
    // Insurer's navy, in a humanist sans with the wide tracking that trade uses.
    bg: '#003781',
    fg: '#ffffff',
    trim: '#ffffff',
    family: '"Gill Sans", "Gill Sans MT", Calibri, Optima, "DejaVu Sans", sans-serif',
    weight: 600,
    tracking: 0.05,
  },
  {
    id: 'olivi',
    name: 'olivi.co',
    // The indigo of the linework in the mark it is taken from, set in the
    // garamond it is written in — the only old-style face on the roster.
    bg: '#ffffff',
    fg: '#3a30dc',
    trim: '#3a30dc',
    family: 'Garamond, "EB Garamond", "Adobe Garamond Pro", "Times New Roman", serif',
    weight: 500,
    tracking: 0.01,
  },
  {
    id: 'galenzhang',
    name: 'galenzhang.com',
    bg: '#ffffff',
    fg: '#101012',
    trim: '#101012',
    family: 'Helvetica, Arial, "DejaVu Sans", sans-serif',
    weight: 400,
    tracking: 0,
  },
];

/** Non-anchors are drawn against these; higher is more often sold. */
const SPONSOR_ODDS: Record<string, number> = {
  'yakutia-motor': 3,
  'visit-yakutsk': 3,
  'galenzhang': 3,
  'olivi': 3,
  'ykt-motors': 2,
  'baishev': 2,
  'aisen': 2,
};

/** Panel sizes, metres. Big enough to read at speed, small enough not to wall
 *  the circuit in — a billboard is about two karts wide and chest high. */
export const BILLBOARD_W = 5.2;
export const BILLBOARD_H = 1.8;
/** Height of the billboard panel's bottom edge above the ground, 3D only. */
export const BILLBOARD_BASE = 1.0;
/** Metres of lap between billboards. */
export const BILLBOARD_SPACING = 110;
/** How far past the barrier a billboard stands. */
export const BILLBOARD_CLEARANCE = 2.6;

/** Barrier ads match the billboards' width; their height is the armco's. */
export const BARRIER_AD_LEN = 6.0;
export const BARRIER_AD_GAP = 4.0;
/** Metres of lap each sponsor holds before the barrier changes hands. */
export const BARRIER_SEGMENT_TARGET = 200;

/** Two corners closer together than this, turning opposite ways, are a chicane. */
export const CHICANE_GAP = 26; // m
/** Painted run-off starts just past the kerb and stops short of the barrier. */
export const GROUND_INNER_PAD = 0.4;
export const GROUND_OUTER_PAD = 1.4;
/** Fraction of the local radius an inward offset may reach before the band
 *  would fold through the centre of curvature. Well under 1 on purpose. */
const FOLD_LIMIT = 0.6;
/** Narrower than this and it is a stripe, not a place to put a logo. */
const MIN_BAND_WIDTH = 2.5;
/** Shortest a wordmark may be shrunk to before it stops being worth reading;
 *  a label this small on a hard corner is still better than one that runs off
 *  its own paint. */
const MIN_LABEL_W = 5;

export interface SponsorBillboard {
  sponsor: Sponsor;
  index: number;
  /** Multiplies `track.normal`: +1 left of the racing direction, -1 right. */
  side: 1 | -1;
  /** Panel centre in world metres. */
  x: number;
  y: number;
  /** Unit tangent, pointing the way the karts travel. */
  tx: number;
  ty: number;
}

/** One stretch of barrier sold to one sponsor. `from`/`to` may wrap the seam. */
export interface BarrierSegment {
  sponsor: Sponsor;
  from: number;
  to: number;
}

/** A single printed panel on the barrier, spanning `from`..`to` inclusive. */
export interface BarrierAd {
  sponsor: Sponsor;
  side: 1 | -1;
  from: number;
  to: number;
  /** Sample indices the panel spans, already unwrapped and in travel order. */
  indices: number[];
}

/** A contiguous painted strip: one entry per sample, no wraps or gaps. */
export interface GroundAdRun {
  indices: number[];
  /** Offset from the centerline of the strip's inner and outer edge, per
   *  sample. Both are magnitudes; `side` says which way they go. */
  inner: number[];
  outer: number[];
}

/** Painted run-off with a wordmark in it, sat outside a corner. */
export interface GroundAd {
  sponsor: Sponsor;
  side: 1 | -1;
  runs: GroundAdRun[];
  /** Sample the wordmark is centred on, and how far out it sits. */
  labelIndex: number;
  labelOffset: number;
  labelW: number;
  labelH: number;
  /** Set when this is the wide painted area outside a chicane. */
  chicane: boolean;
}

export interface TrackSponsorship {
  sponsors: Sponsor[];
  billboards: SponsorBillboard[];
  segments: BarrierSegment[];
  barrierAds: BarrierAd[];
  groundAds: GroundAd[];
}

/** Number of samples spanning `metres` of track. */
function steps(track: Track, metres: number): number {
  return Math.max(1, Math.round(metres / track.spacing));
}

/** Forward distance in samples from `a` to `b` around the ring. */
function forward(a: number, b: number, n: number): number {
  return (b - a + n) % n;
}

/**
 * Distance from a point to the nearest centerline sample anywhere on the lap.
 *
 * A compact circuit doubles back on itself, and an offset far enough out to
 * clear the barrier can easily land on a different straight. Everything placed
 * off the racing surface is checked against this before it is kept, which is
 * what stops a billboard from being planted in the middle of the road on a
 * tight layout — the one failure here that would be a real bug rather than a
 * cosmetic one. O(n) per candidate, run once at world load.
 */
function distanceToTrack(track: Track, x: number, y: number): number {
  let best = Infinity;
  for (const p of track.center) {
    const dx = p.x - x;
    const dy = p.y - y;
    const d = dx * dx + dy * dy;
    if (d < best) best = d;
  }
  return Math.sqrt(best);
}

/**
 * How close to the circuit anything sponsored is allowed to come: one metre
 * past the outside of the kerb, anywhere on the lap.
 *
 * A misplaced advert is worse than a missing one, so everything measured
 * against this is *dropped* when it fails rather than nudged somewhere else.
 * Circuits that fold back on themselves inside thirty metres — Suzuka across
 * its own flyover, Jerez's tighter returns — simply go quiet for a stretch,
 * which is what a real circuit does when there is nowhere to hang a board.
 */
export const SPONSOR_CLEARANCE = 1.0;

function offTrackClearance(track: Track): number {
  return track.width / 2 + track.kerbWidth + SPONSOR_CLEARANCE;
}

/**
 * Whether an offset ring still runs forwards between two samples.
 *
 * Offset far enough toward a corner's centre of curvature and the ring turns
 * inside out: the barrier line through the inside of a hairpin doubles back on
 * itself, and anything printed along it is a mangled ribbon rather than a
 * board. Tested on the real offset points, because `curvature` is a smoothed
 * series and understates how hard a sample bends.
 */
function offsetAdvances(track: Track, i: number, j: number, offset: number, side: 1 | -1): boolean {
  const c = track.center;
  const nm = track.normal;
  const dx = c[j].x - c[i].x;
  const dy = c[j].y - c[i].y;
  const ax = c[i].x + nm[i].x * offset * side;
  const ay = c[i].y + nm[i].y * offset * side;
  const bx = c[j].x + nm[j].x * offset * side;
  const by = c[j].y + nm[j].y * offset * side;
  return (bx - ax) * dx + (by - ay) * dy > 0.15 * (dx * dx + dy * dy);
}

/** Where the printed face of a barrier panel sits, measured from the centerline. */
export function barrierAdOffset(track: Track): number {
  return track.width / 2 + track.kerbWidth + track.wallMargin - 0.3;
}

/** How many names one circuit sells to. A longer lap carries more without
 *  either of them getting a stretch too short to read. */
export function sponsorSlots(track: Track): number {
  const anchors = SPONSORS.filter((s) => s.anchor).length;
  const extra = Math.round(track.length / 500);
  return Math.max(anchors + 1, Math.min(6, anchors + extra));
}

/**
 * Which sponsors this circuit sells to, in the order they take the barrier.
 *
 * The anchors are on every circuit and come first, which is not a formality:
 * segments are dealt from the start line, so first in this list is the name on
 * the pit straight, and {@link placeGroundAds} works down the same list, so the
 * anchors also get the painted corners. Everyone else is drawn against
 * {@link SPONSOR_ODDS} without replacement, so a circuit carries its own mix
 * rather than the whole roster — which is both how a real paddock looks and
 * what keeps each name's stretch of barrier long enough to read.
 */
export function sponsorsForTrack(track: Track): Sponsor[] {
  const rng = mulberry32(fnv1a(`karting-hotlap:sponsors:${track.key}`));

  const shuffle = (list: Sponsor[]): Sponsor[] => {
    for (let i = list.length - 1; i > 0; i--) {
      const j = rng.int(0, i + 1);
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  };

  const chosen = shuffle(SPONSORS.filter((s) => s.anchor));
  const pool = SPONSORS.filter((s) => !s.anchor);
  const slots = sponsorSlots(track);

  // Weighted draw without replacement: walk the running total and take whoever
  // the roll lands on, then take that one out of the pool.
  while (chosen.length < slots && pool.length) {
    let total = 0;
    for (const s of pool) total += SPONSOR_ODDS[s.id] ?? 1;
    let roll = rng.range(0, total);
    let pick = pool.length - 1;
    for (let i = 0; i < pool.length; i++) {
      roll -= SPONSOR_ODDS[pool[i].id] ?? 1;
      if (roll < 0) {
        pick = i;
        break;
      }
    }
    chosen.push(pool[pick]);
    pool.splice(pick, 1);
  }
  return chosen;
}

/**
 * Cut the lap into equal barrier segments, one sponsor each, dealt round-robin
 * from the start line. Every sponsor gets the same number of segments, so a
 * long circuit sells each of them more barrier rather than crowding extra
 * names on.
 */
export function barrierSegments(track: Track, sponsors: Sponsor[]): BarrierSegment[] {
  const n = track.center.length;
  if (!sponsors.length) return [];
  const each = Math.max(1, Math.round(track.length / (sponsors.length * BARRIER_SEGMENT_TARGET)));
  const count = sponsors.length * each;
  const out: BarrierSegment[] = [];
  for (let s = 0; s < count; s++) {
    const from = wrapIndex(track.startIndex + Math.round((s * n) / count), n);
    const next = wrapIndex(track.startIndex + Math.round(((s + 1) * n) / count), n);
    out.push({ sponsor: sponsors[s % sponsors.length], from, to: wrapIndex(next - 1, n) });
  }
  return out;
}

/** The segment covering a sample, or null when the ring is unsold. */
export function segmentAt(segments: BarrierSegment[], index: number, n: number): BarrierSegment | null {
  for (const seg of segments) {
    const span = forward(seg.from, seg.to, n);
    if (forward(seg.from, index, n) <= span) return seg;
  }
  return null;
}

/**
 * Lay printed panels along each segment's barrier, both sides, evenly pitched —
 * and only where there is actually room for one.
 *
 * The barrier ring runs ten metres past the kerb, which on a circuit that comes
 * back alongside itself puts a stretch of it over a different straight, and on
 * the inside of a hairpin turns it inside out. Both were shipped unguarded and
 * both are visible from the car: a board apparently standing on the road, or a
 * wordmark smeared through a corner. A panel that fails either test is dropped
 * whole rather than trimmed — half a board is no better than a misplaced one —
 * and the hoarding simply has a gap there, as it would in life.
 */
export function barrierAds(track: Track, segments: BarrierSegment[]): BarrierAd[] {
  const n = track.center.length;
  const adSteps = steps(track, BARRIER_AD_LEN);
  const pitch = adSteps + steps(track, BARRIER_AD_GAP);
  const dist = barrierAdOffset(track);
  const clear = offTrackClearance(track);
  const out: BarrierAd[] = [];

  const fits = (indices: number[], side: 1 | -1): boolean => {
    for (let k = 0; k < indices.length; k++) {
      const i = indices[k];
      const x = track.center[i].x + track.normal[i].x * dist * side;
      const y = track.center[i].y + track.normal[i].y * dist * side;
      if (distanceToTrack(track, x, y) < clear) return false;
      if (k + 1 < indices.length && !offsetAdvances(track, i, indices[k + 1], dist, side)) return false;
    }
    return true;
  };

  for (const seg of segments) {
    const span = forward(seg.from, seg.to, n);
    for (let offset = 0; offset + adSteps <= span; offset += pitch) {
      const indices: number[] = [];
      for (let k = 0; k <= adSteps; k++) indices.push(wrapIndex(seg.from + offset + k, n));
      for (const side of [1, -1] as const) {
        if (!fits(indices, side)) continue;
        out.push({
          sponsor: seg.sponsor,
          side,
          from: indices[0],
          to: indices[indices.length - 1],
          indices,
        });
      }
    }
  }
  return out;
}

/**
 * Freestanding billboards, spaced around the lap outside the barrier.
 *
 * Always on the outside of whatever the track is doing there, which is both
 * where a driver's eyes already are and the only side where standing a panel
 * that far off the centerline cannot fold through a corner's own radius. The
 * `FOLD_LIMIT` check below is belt-and-braces for the near-straight case, where
 * the side is chosen by a sign that means almost nothing.
 */
export function placeBillboards(
  track: Track,
  segments: BarrierSegment[],
): SponsorBillboard[] {
  const n = track.center.length;
  const { center, normal, curvature, width, kerbWidth, wallMargin } = track;
  const offset = width / 2 + kerbWidth + wallMargin + BILLBOARD_CLEARANCE;
  const stride = steps(track, BILLBOARD_SPACING);
  const window = steps(track, 12);
  const out: SponsorBillboard[] = [];

  for (let s = Math.round(stride / 2); s < n; s += stride) {
    const i = wrapIndex(track.startIndex + s, n);

    // Average curvature over a corner's worth of track, so a single noisy
    // sample on a straight cannot decide which side the board stands.
    let sum = 0;
    for (let w = -window; w <= window; w++) sum += curvature[wrapIndex(i + w, n)];
    const kAvg = sum / (window * 2 + 1);

    let side: 1 | -1 = kAvg >= 0 ? -1 : 1;
    const kHere = curvature[i];
    // `side * k > 0` means the panel is being pushed toward the centre of the
    // local circle; past FOLD_LIMIT of the radius it would end up inside it.
    if (side * kHere > 0 && offset > FOLD_LIMIT / Math.abs(kHere)) side = -side as 1 | -1;

    const prev = center[wrapIndex(i - 1, n)];
    const next = center[wrapIndex(i + 1, n)];
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const len = Math.hypot(tx, ty) || 1;

    // Try the chosen side, then the other one, then give up on this spot — a
    // circuit is allowed to be too tightly wound to sell every slot.
    //
    // The whole panel is measured, not its centre. A five-metre board planted
    // clear at the middle can still have an end over the road where the track
    // comes back past it, and an end over the road is exactly the thing that
    // reads as a bug.
    const at = (s2: 1 | -1): { x: number; y: number } => ({
      x: center[i].x + normal[i].x * offset * s2,
      y: center[i].y + normal[i].y * offset * s2,
    });
    const clear = offTrackClearance(track);
    const fits = (s2: 1 | -1): boolean => {
      const p = at(s2);
      for (let t = -0.5; t <= 0.5001; t += 0.25) {
        const x = p.x + (-ty / len) * BILLBOARD_W * t;
        const y = p.y + (tx / len) * BILLBOARD_W * t;
        if (distanceToTrack(track, x, y) < clear) return false;
      }
      return true;
    };

    if (!fits(side)) {
      side = -side as 1 | -1;
      if (!fits(side)) continue;
    }
    const spot = at(side);

    const seg = segmentAt(segments, i, n);
    if (!seg) continue;

    out.push({ sponsor: seg.sponsor, index: i, side, x: spot.x, y: spot.y, tx: tx / len, ty: ty / len });
  }
  return out;
}

interface CornerSite {
  from: number;
  to: number;
  side: 1 | -1;
  chicane: boolean;
  /** Slower is a better site: more time looking at it. */
  vApex: number;
}

/**
 * Whether one `Corner` is itself a chicane, and which way its first half turns.
 *
 * `findCorners` merges runs less than `MERGE_GAP` apart into one corner
 * regardless of which way they bend, and a chicane's two halves are exactly
 * that close — so on Monza the first chicane arrives here as a single corner
 * whose curvature changes sign halfway through, not as a pair. Miss this and
 * the feature this whole thing was asked for never fires on the circuit it was
 * modelled on.
 */
function chicaneWithin(track: Track, from: number, to: number): 1 | -1 | null {
  const n = track.center.length;
  const span = forward(from, to, n);
  const min = 4; // samples of each hand before it is a direction, not noise
  let pos = 0;
  let neg = 0;
  let first: 1 | -1 | null = null;
  for (let s = 0; s <= span; s++) {
    const k = track.curvature[wrapIndex(from + s, n)];
    if (k > 0.01) {
      pos++;
      if (first === null && pos >= min) first = 1;
    } else if (k < -0.01) {
      neg++;
      if (first === null && neg >= min) first = -1;
    }
  }
  return pos >= min && neg >= min ? first : null;
}

/**
 * Corners worth painting, best first.
 *
 * Chicanes lead, because the wide painted apron wrapped around a chicane's
 * outside is the real-world thing this is copying — Monza's first chicane has
 * exactly that, a curved sponsor field between kerb and barrier. After those,
 * the slowest remaining corners, where a driver is on track long enough to
 * read something.
 */
function cornerSites(track: Track, corners: Corner[]): CornerSite[] {
  const n = track.center.length;
  const pad = steps(track, 6);
  const chicaneSteps = steps(track, CHICANE_GAP);
  const used = new Set<number>();
  const sites: CornerSite[] = [];

  // A chicane the corner finder kept whole.
  for (let i = 0; i < corners.length; i++) {
    const c = corners[i];
    const first = chicaneWithin(track, c.entryIndex, c.exitIndex);
    if (first === null) continue;
    used.add(i);
    sites.push({
      from: wrapIndex(c.entryIndex - pad, n),
      to: wrapIndex(c.exitIndex + pad, n),
      // Outside of the first half — straight on from the braking zone, which is
      // where a chicane's run-off is and where a driver who missed it ends up.
      side: -first as 1 | -1,
      chicane: true,
      vApex: c.vApex,
    });
  }

  // A chicane that arrived as two corners close together, turning opposite ways.
  for (let i = 0; i < corners.length; i++) {
    const j = (i + 1) % corners.length;
    if (corners.length < 2 || used.has(i) || used.has(j)) continue;
    const a = corners[i];
    const b = corners[j];
    if (a.direction === b.direction) continue;
    if (forward(a.exitIndex, b.entryIndex, n) > chicaneSteps) continue;
    used.add(i);
    used.add(j);
    sites.push({
      from: wrapIndex(a.entryIndex - pad, n),
      to: wrapIndex(b.exitIndex + pad, n),
      side: -a.direction as 1 | -1,
      chicane: true,
      vApex: Math.min(a.vApex, b.vApex),
    });
  }

  const singles: CornerSite[] = [];
  for (let i = 0; i < corners.length; i++) {
    if (used.has(i)) continue;
    const c = corners[i];
    singles.push({
      from: wrapIndex(c.entryIndex - pad, n),
      to: wrapIndex(c.exitIndex + pad, n),
      side: -c.direction as 1 | -1,
      chicane: false,
      vApex: c.vApex,
    });
  }
  singles.sort((p, q) => p.vApex - q.vApex);
  return [...sites, ...singles];
}

/**
 * Split a candidate site into the contiguous stretches where a painted band
 * actually fits, and give each stretch one constant width.
 *
 * Two things can make a sample unusable. The track may have turned back the
 * other way underneath the band, putting the chosen side on the *inside* of a
 * turn — reach past `FOLD_LIMIT` of the local radius there and the offset ring
 * folds through its own centre of curvature and paints a bowtie. Or the band
 * may have reached across onto a different part of a compact circuit.
 *
 * The width is then the tightest sample's width for the whole run rather than
 * per-sample, which is both what real run-off paint looks like and the only
 * way adjacent quads cannot collapse into slivers where the limit starts
 * binding.
 */
function bandRuns(track: Track, site: CornerSite, inner: number, outer: number): GroundAdRun[] {
  const n = track.center.length;
  const span = forward(site.from, site.to, n);
  const clear = offTrackClearance(track);
  const runs: Array<{ indices: number[]; widest: number }> = [];
  let cur: { indices: number[]; widest: number } | null = null;

  for (let s = 0; s <= span; s++) {
    const i = wrapIndex(site.from + s, n);
    const k = track.curvature[i];
    const inward = site.side * k > 0;
    const limit = inward && Math.abs(k) > 1e-9 ? FOLD_LIMIT / Math.abs(k) : Infinity;
    let fit = Math.min(outer, limit);

    // Walk the outer edge in until it is off anything else on the circuit.
    const at = (idx: number, d: number): { x: number; y: number } => ({
      x: track.center[idx].x + track.normal[idx].x * d * site.side,
      y: track.center[idx].y + track.normal[idx].y * d * site.side,
    });
    const edgeAt = (d: number): number => {
      const p = at(i, d);
      return distanceToTrack(track, p.x, p.y);
    };

    // Direct fold test, on the real offset points rather than on `curvature`,
    // which is a smoothed series and understates how hard a sample actually
    // bends. An edge that has folded runs *backwards* against the centerline it
    // was offset from, so requiring forward progress catches it exactly.
    const j = wrapIndex(i + 1, n);
    const dx = track.center[j].x - track.center[i].x;
    const dy = track.center[j].y - track.center[i].y;
    const step2 = dx * dx + dy * dy;
    const advances = (d: number): boolean => {
      const a = at(i, d);
      const b = at(j, d);
      return (b.x - a.x) * dx + (b.y - a.y) * dy > 0.15 * step2;
    };

    while (fit - inner >= MIN_BAND_WIDTH && (edgeAt(fit) < clear || !advances(fit))) fit -= 1;
    if (!advances(inner)) fit = inner;

    if (fit - inner < MIN_BAND_WIDTH) {
      cur = null;
      continue;
    }
    if (!cur) {
      cur = { indices: [], widest: fit };
      runs.push(cur);
    }
    cur.indices.push(i);
    cur.widest = Math.min(cur.widest, fit);
  }

  // A strip under four samples is a sliver, not signage.
  return runs
    .filter((r) => r.indices.length >= 4)
    .map((r) => ({
      indices: r.indices,
      inner: r.indices.map(() => inner),
      outer: r.indices.map(() => r.widest),
    }));
}

/**
 * Does a label of this size, laid flat on the tangent at `labelIndex`, stay
 * inside the paint it is supposed to be printed on?
 *
 * The band is an arc — ground ads only ever sit outside a corner — while both
 * renderers draw the wordmark as a straight rectangle. Size it off the run's
 * arc length, as this used to, and the ends of a 16 m label swing off the
 * paint and onto the grass. The panel is drawn with no background of its own
 * (the painted area *is* its background), so those letters land as bare
 * coloured glyphs on green: "Aldan Tyres" is blue on grass, which reads as a
 * wordmark cut off mid-word rather than one that overshot.
 */
function labelFits(
  track: Track,
  grid: PointGrid,
  run: GroundAdRun,
  side: 1 | -1,
  labelIndex: number,
  offset: number,
  w: number,
  h: number,
): boolean {
  const n = track.center.length;
  const prev = track.center[wrapIndex(labelIndex - 1, n)];
  const next = track.center[wrapIndex(labelIndex + 1, n)];
  let tx = next.x - prev.x;
  let ty = next.y - prev.y;
  const len = Math.hypot(tx, ty);
  if (len < 1e-9) return false;
  tx /= len;
  ty /= len;
  const cx = track.center[labelIndex].x + track.normal[labelIndex].x * offset * side;
  const cy = track.center[labelIndex].y + track.normal[labelIndex].y * offset * side;

  for (const u of [-0.5, -0.25, 0.25, 0.5]) {
    for (const v of [-0.5, 0.5]) {
      // The label's short axis is the tangent's perpendicular, not the
      // centerline normal — the two diverge exactly where this matters.
      const x = cx + tx * w * u - ty * h * v;
      const y = cy + ty * w * u + tx * h * v;

      let bestS = -1;
      let bestD = Infinity;
      for (let s = 0; s < run.indices.length; s++) {
        const p = track.center[run.indices[s]];
        const d = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (d < bestD) {
          bestD = d;
          bestS = s;
        }
      }
      if (bestS < 0) return false;
      // The nearest sample *in the run* must also be the nearest sample on the
      // circuit. Where it is not, the corner has swung past the end of its own
      // band onto ground that belongs to another stretch of road — which is
      // off the paint just as surely as overshooting it sideways.
      const own = Math.sqrt(bestD);
      if (grid.nearestWithin(x, y, own) < own - 1e-6) return false;
      const i = run.indices[bestS];
      const mag =
        (x - track.center[i].x) * track.normal[i].x * side +
        (y - track.center[i].y) * track.normal[i].y * side;
      if (mag < run.inner[bestS] || mag > run.outer[bestS]) return false;
    }
  }
  return true;
}

/** Painted, sponsored run-off outside the circuit's best corners. */
export function placeGroundAds(track: Track, corners: Corner[], sponsors: Sponsor[]): GroundAd[] {
  if (!sponsors.length) return [];
  const inner = track.width / 2 + track.kerbWidth + GROUND_INNER_PAD;
  const outer = track.width / 2 + track.kerbWidth + track.wallMargin - GROUND_OUTER_PAD;
  if (outer - inner < MIN_BAND_WIDTH) return [];

  const wanted = Math.min(sponsors.length, Math.max(2, Math.floor(track.length / 400)));
  const out: GroundAd[] = [];
  const grid = new PointGrid(track.center, 24);

  for (const site of cornerSites(track, corners)) {
    if (out.length >= wanted) break;
    const runs = bandRuns(track, site, inner, outer);
    if (!runs.length) continue;

    // Wordmark goes in the longest stretch, halfway along and halfway out.
    let best = runs[0];
    for (const r of runs) if (r.indices.length > best.indices.length) best = r;
    const mid = Math.floor(best.indices.length / 2);
    const bandWidth = best.outer[mid] - best.inner[mid];
    const runLength = best.indices.length * track.spacing;

    const labelIndex = best.indices[mid];
    const labelOffset = best.inner[mid] + bandWidth / 2;
    const labelH = Math.min(bandWidth * 0.5, 4.5);

    // Shrink along the track until the whole wordmark is on the paint. The
    // arc length is only an upper bound — how much of it a straight label can
    // actually use depends on how hard the band is turning underneath it.
    let labelW = Math.min(runLength * 0.6, 16);
    while (labelW > MIN_LABEL_W && !labelFits(track, grid, best, site.side, labelIndex, labelOffset, labelW, labelH)) {
      labelW -= 0.5;
    }

    out.push({
      sponsor: sponsors[out.length % sponsors.length],
      side: site.side,
      runs,
      labelIndex,
      labelOffset,
      labelW: Math.max(labelW, MIN_LABEL_W),
      labelH,
      chicane: site.chicane,
    });
  }
  return out;
}

/** Everything a renderer needs to dress a circuit, built once per world load. */
export function buildSponsorship(track: Track, corners: Corner[]): TrackSponsorship {
  const sponsors = sponsorsForTrack(track);
  const segments = barrierSegments(track, sponsors);
  return {
    sponsors,
    segments,
    billboards: placeBillboards(track, segments),
    barrierAds: barrierAds(track, segments),
    groundAds: placeGroundAds(track, corners, sponsors),
  };
}
